/**
 * Authentication Provider
 * Manages authentication state, auto-refresh, and 401 handling
 */

import React, { createContext, useContext, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth';
import { useRefreshToken, useLogout } from '../../api/hooks/useAuth';
import type { LoginResponse, UserProfile } from '../../api/types';

export interface AuthContextValue {
  // State
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  login: (data: LoginResponse) => void;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * AuthProvider - Wraps app with authentication context
 *
 * Features:
 * - Auto-refresh tokens before expiry (5 min buffer)
 * - Redirect to /login on 401
 * - Provide auth state and actions to children
 *
 * Note: API client is initialized in main.tsx, not here
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const navigate = useNavigate();
  const {
    accessToken,
    refreshToken: storedRefreshToken,
    user,
    setTokens,
    clearTokens,
    setUser,
    shouldRefresh,
    isTokenExpired,
  } = useAuthStore();

  const refreshMutation = useRefreshToken();
  const logoutMutation = useLogout();

  // Track if we're currently refreshing to prevent multiple refresh calls
  const isRefreshingRef = useRef(false);

  // Check if authenticated (has valid token)
  const isAuthenticated = Boolean(accessToken && !isTokenExpired());

  // Auto-refresh token before expiry
  useEffect(() => {
    if (!accessToken || !storedRefreshToken || !shouldRefresh()) {
      return;
    }

    // Prevent multiple concurrent refreshes
    if (isRefreshingRef.current) {
      return;
    }

    const performRefresh = async () => {
      isRefreshingRef.current = true;

      try {
        const response = await refreshMutation.mutateAsync({
          refresh_token: storedRefreshToken,
        });

        // Update tokens in store (keep existing refresh token)
        setTokens(response.accessToken, storedRefreshToken, response.expiresIn);
      } catch (error) {
        // Refresh failed - clear tokens and redirect to login
        console.error('Token refresh failed:', error);
        clearTokens();
        navigate('/login', { replace: true });
      } finally {
        isRefreshingRef.current = false;
      }
    };

    performRefresh();
  }, [accessToken, storedRefreshToken, shouldRefresh, refreshMutation, setTokens, clearTokens, navigate]);

  // Set up refresh interval (check every minute)
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      if (shouldRefresh() && !isRefreshingRef.current) {
        // Trigger re-render to run refresh effect
        useAuthStore.setState({});
      }
    }, 60 * 1000); // Check every minute

    return () => clearInterval(interval);
  }, [isAuthenticated, shouldRefresh]);

  // Login action (called after successful OAuth)
  const login = useCallback(
    (data: LoginResponse) => {
      const { accessToken, refreshToken, expiresIn, user } = data;
      setTokens(accessToken, refreshToken, expiresIn);
      setUser(user);
    },
    [setTokens, setUser]
  );

  // Logout action
  const logout = useCallback(async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch (error) {
      // Log error but continue with logout
      console.error('Logout API call failed:', error);
    } finally {
      // Always clear tokens and redirect
      clearTokens();
      navigate('/login', { replace: true });
    }
  }, [logoutMutation, clearTokens, navigate]);

  // Manual refresh action
  const refreshTokenAction = useCallback(async () => {
    if (!storedRefreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await refreshMutation.mutateAsync({
      refresh_token: storedRefreshToken,
    });

    setTokens(response.accessToken, storedRefreshToken, response.expiresIn);
  }, [storedRefreshToken, refreshMutation, setTokens]);

  const value: AuthContextValue = {
    user,
    isAuthenticated,
    isLoading: refreshMutation.isPending || logoutMutation.isPending,
    login,
    logout,
    refreshToken: refreshTokenAction,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context
 * @throws Error if used outside AuthProvider
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
